import _ from 'lodash';
import { getTimezoneOffset } from 'date-fns-tz';
import { capitalizeFirstLetter } from '@travelity/utils';
import {
    Action,
    ContentType,
    GetProductsResItem0AttachmentsItemDto,
    ListItemAction,
    PeriodData,
    PersonNameData,
    PhoneNumberData,
    TimeOffset,
    type UpdateUserReqProfileAttachmentsItemDto,
    UpdateUserReqProfileAttachmentsItemReference0Dto,
} from '../generated';
import { Activity, DurationValue, Period } from './common.types';
import { Attachment } from './customer/customer.types';

// Duration
export const convertDurationToDurationDto = (d: DurationValue): TimeOffset => ({
    days: d.day || 0,
    hours: d.hour || 0,
    minutes: d.minute || 0,
});

export const convertDurationDtoToDuration = (
    d: Partial<TimeOffset>
): DurationValue => ({
    day: d.days || 0,
    hour: d.hours || 0,
    minute: d.minutes || 0,
});

export const getStatusChangeReason = (
    lifecycle: Action[] = [],
    status: string = ''
): string => {
    return (
        lifecycle?.findLast(l =>
            l.updates?.some(
                ({ name, value }) => name === 'status' && value === status
            )
        )?.message || ''
    );
};

export const isOrderChanged = (oldIds: string[], newIds: string[]) => {
    const hasNewInMiddle = newIds.reduce(
        (prevNew, id, i) =>
            prevNew ||
            (!!i && oldIds.includes(id) && !oldIds.includes(newIds[i - 1])),
        false
    );
    if (hasNewInMiddle) return true;

    const commonItems = oldIds.filter(id => newIds.includes(id));
    const commonItemsSameOrder = commonItems.length
        ? commonItems.reduce(
              (prevIndex, id) =>
                  newIds.indexOf(id) > prevIndex
                      ? newIds.indexOf(id)
                      : newIds.length,
              -1
          ) < newIds.length
        : true;

    return !commonItemsSameOrder;
};

export const convertTimeOffsetToMinutes = (timeOffset?: TimeOffset): number => {
    return timeOffset
        ? (timeOffset.days || 0) * 60 * 24 +
              (timeOffset.hours || 0) * 60 +
              (timeOffset.minutes || 0)
        : 0;
};

export const convertItemsToActionItemsDto = <T extends Record<string, any>>(
    oldItems: T[],
    newItems: T[],
    idPath: string,
    isUpdated: (oldItem: T, newItem: T) => boolean = () => false
): ({ action: ListItemAction } & T)[] => {
    const orderChanged = isOrderChanged(
        oldItems.map(item => _.get(item, idPath)),
        newItems.map(item => _.get(item, idPath))
    );

    if (orderChanged) {
        return newItems.map(item => ({
            ...item,
            action: ListItemAction.REORDER,
        }));
    }

    const addedItems = newItems.filter(
        newItem =>
            !oldItems.find(
                oldItem => _.get(oldItem, idPath) === _.get(newItem, idPath)
            )
    );

    const removedItems = oldItems.filter(
        oldItem =>
            !newItems.find(
                newItem => _.get(newItem, idPath) === _.get(oldItem, idPath)
            )
    );

    const updatedItems = newItems.filter(newItem =>
        oldItems.find(
            oldItem =>
                _.get(newItem, idPath) === _.get(oldItem, idPath) &&
                isUpdated(oldItem, newItem)
        )
    );

    return [
        ...addedItems.map(item => ({
            ...item,
            action: ListItemAction.ADD,
        })),
        ...removedItems.map(item => ({
            ...item,
            action: ListItemAction.REMOVE,
        })),
        ...updatedItems.map(item => ({
            ...item,
            action: ListItemAction.UPDATE,
        })),
    ];
};

const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const convertDateToTimestamp = (
    d: Date,
    skipOffset: boolean = false,
    timeZone: string = localTz
): number => {
    const offset = getTimezoneOffset(timeZone);
    return Math.round((d.getTime() + (skipOffset ? 0 : offset)) / 1000);
};

export const convertTimestampToDate = (d: number): Date => {
    return new Date(d * 1000);
};

export const convertPeriodDataToPeriod = (data: PeriodData): Period => ({
    timezone: data.timezone?.name,
    duration: convertDurationDtoToDuration(data.duration || {}),
    start: convertTimestampToDate(data.start),
    end: data.end ? convertTimestampToDate(data.end) : undefined,
});

export const convertPeriodToPeriodData = (period: Period): PeriodData => ({
    duration: convertDurationToDurationDto(period.duration),
    start: convertDateToTimestamp(period.start),
    end: period.end ? convertDateToTimestamp(period.end) : undefined,
});

export const convertActivityDtoToActivity = (activity: Action): Activity => ({
    type: activity.type,
    name: activity.by.name as PersonNameData,
    date: convertTimestampToDate(activity.at),
    updates:
        activity.updates
            ?.filter(({ name }) => !!name)
            .map(({ name }) =>
                name
                    .split('.')
                    .map(n => capitalizeFirstLetter(n.replaceAll('_', ' ')))
                    .join('➝')
            ) || [],
    message: activity.message,
});

export const phoneNumberDataToPhoneNumber = (
    phoneNumber: PhoneNumberData
): string => {
    return `+${phoneNumber.calling_code} ${phoneNumber.number}`;
};

export const filterDateValueToAt = (date?: {
    startDate: Date;
    endDate: Date;
}): (number | null)[] | undefined => {
    return date
        ? [
              date.startDate ? convertDateToTimestamp(date.startDate) : null,
              date.endDate ? convertDateToTimestamp(date.endDate) : null,
          ]
        : undefined;
};

export const convertAttachmentToAttachmentDto = (
    attachment: Attachment
): Omit<UpdateUserReqProfileAttachmentsItemDto, 'action'> => ({
    id: attachment.uuid,
    reference: {
        name: attachment.name,
        content_type: (attachment.contentType as ContentType) || undefined,
        content: attachment.content,
        width: attachment.width,
        height: attachment.height,
    },
});

export const convertAttachmentDtoToAttachment = (
    attachment: Omit<GetProductsResItem0AttachmentsItemDto, 'action'>
): Attachment => ({
    uuid: attachment.id,
    name: attachment.reference!.name || '',
    contentType: attachment.reference!.content_type || '',
    content: '',
    url: attachment.reference!.url,
    width: (attachment.reference as UpdateUserReqProfileAttachmentsItemReference0Dto)!
        .width,
    height: (attachment.reference as UpdateUserReqProfileAttachmentsItemReference0Dto)!
        .height,
});
